import { ThreeEvent, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Plane, Vector3 } from 'three';
import { getCameraAlignedDragPlane } from 'utils/3d-drag-plane';

const DRAG_THRESHOLD_PX = 5;

export type DirectPointDragHandlers = {
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
  onPointerCancel: (event: ThreeEvent<PointerEvent>) => void;
};

type UseDirectPointDragArgs = {
  pointPosition: [number, number, number];
  isBlocked?: () => boolean;
  onDragStart: () => void;
  onDragChange: (localPosition: Vector3) => void;
  onDragEnd: (localPosition: Vector3 | null) => void;
};

type ControlsLike = { enabled: boolean };

type DragState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  plane: Plane;
  grabOffset: Vector3;
  worldToLocal: Vector3;
  target: Vector3;
  dragging: boolean;
};

export function useDirectPointDrag({
  pointPosition,
  isBlocked,
  onDragStart,
  onDragChange,
  onDragEnd
}: UseDirectPointDragArgs): DirectPointDragHandlers {
  const camera = useThree((state) => state.camera);
  const controls = useThree((state) => state.controls);

  const pointPositionRef = useRef(pointPosition);
  const cameraRef = useRef(camera);
  const controlsRef = useRef(controls);
  const callbacksRef = useRef({
    isBlocked,
    onDragStart,
    onDragChange,
    onDragEnd
  });
  const dragStateRef = useRef<DragState | null>(null);

  pointPositionRef.current = pointPosition;
  cameraRef.current = camera;
  controlsRef.current = controls;
  callbacksRef.current = { isBlocked, onDragStart, onDragChange, onDragEnd };

  const setControlsEnabled = useCallback((enabled: boolean) => {
    const orbitControls = controlsRef.current as unknown as ControlsLike | null;
    if (orbitControls) {
      orbitControls.enabled = enabled;
    }
  }, []);

  const finishDrag = useCallback(
    (commitTarget: Vector3 | null) => {
      const state = dragStateRef.current;
      if (!state) return;
      dragStateRef.current = null;
      setControlsEnabled(true);
      if (state.dragging) {
        document.body.style.cursor = 'grab';
        callbacksRef.current.onDragEnd(commitTarget);
      }
    },
    [setControlsEnabled]
  );

  useEffect(() => {
    return () => {
      const state = dragStateRef.current;
      if (state) {
        dragStateRef.current = null;
        setControlsEnabled(true);
        if (state.dragging) {
          document.body.style.cursor = '';
          callbacksRef.current.onDragEnd(null);
        }
      }
    };
  }, [setControlsEnabled]);

  const resolveLocalTarget = useCallback(
    (event: ThreeEvent<PointerEvent>, state: DragState): Vector3 | null => {
      const hit = event.ray.intersectPlane(state.plane, state.target);
      if (!hit) return null;
      return hit.sub(state.grabOffset).add(state.worldToLocal);
    },
    []
  );

  const onPointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (event.button !== 0) return;
      if (dragStateRef.current) return;
      if (callbacksRef.current.isBlocked?.()) return;

      const worldPoint = event.object.getWorldPosition(new Vector3());
      const plane = getCameraAlignedDragPlane(cameraRef.current, worldPoint);
      const hit = event.ray.intersectPlane(plane, new Vector3());
      if (!hit) return;

      const [localX, localY, localZ] = pointPositionRef.current;
      const worldToLocal = new Vector3(localX, localY, localZ).sub(worldPoint);

      dragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.nativeEvent.clientX,
        startClientY: event.nativeEvent.clientY,
        plane,
        grabOffset: hit.clone().sub(worldPoint),
        worldToLocal,
        target: new Vector3(),
        dragging: false
      };

      const target = event.target as unknown as {
        setPointerCapture?: (pointerId: number) => void;
      };
      target.setPointerCapture?.(event.pointerId);
      setControlsEnabled(false);
    },
    [setControlsEnabled]
  );

  const onPointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;

      if (!state.dragging) {
        const distance = Math.hypot(
          event.nativeEvent.clientX - state.startClientX,
          event.nativeEvent.clientY - state.startClientY
        );
        if (distance < DRAG_THRESHOLD_PX) return;
        state.dragging = true;
        document.body.style.cursor = 'grabbing';
        callbacksRef.current.onDragStart();
      }

      const localTarget = resolveLocalTarget(event, state);
      if (localTarget) {
        callbacksRef.current.onDragChange(localTarget);
      }
    },
    [resolveLocalTarget]
  );

  const onPointerUp = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      if (event.button !== 0) return;

      const target = event.target as unknown as {
        releasePointerCapture?: (pointerId: number) => void;
      };
      target.releasePointerCapture?.(event.pointerId);

      const commitTarget = state.dragging
        ? resolveLocalTarget(event, state)
        : null;
      finishDrag(commitTarget);
    },
    [resolveLocalTarget, finishDrag]
  );

  const onPointerCancel = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      finishDrag(null);
    },
    [finishDrag]
  );

  return useMemo(
    () => ({ onPointerDown, onPointerMove, onPointerUp, onPointerCancel }),
    [onPointerDown, onPointerMove, onPointerUp, onPointerCancel]
  );
}
